import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Mail,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { onboardingApi, type DnsLookupResult, type DnsStatusResponse } from "@/lib/api";
import {
  OnboardingTimeline,
  itemsFromDnsStatus,
} from "../components/OnboardingTimeline";

// Wizard terminal. Polls /dns-status on a 30-second auto-refresh and renders
// the provisioning timeline. There is intentionally NO "Open my workspace"
// button here — the canonical path from notification to the workspace is the
// "subdomain ready" email's tracked CTA. The user can safely close this tab;
// the email is the durable signal and we record clicks.
const POLL_INTERVAL_MS = 30_000;

export function StepComplete() {
  const { idToken } = useAuth();
  const onboardingId = useOnboardingStore((s) => s.onboardingId);
  const subdomain = useOnboardingStore((s) => s.subdomain);
  const email = useOnboardingStore((s) => s.email);

  const [status, setStatus] = useState<DnsStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const inFlightRef = useRef(false);

  // True when EVERY provisioning timestamp has been observed by /dns-status.
  // We deliberately do NOT gate purely on `stage === "subdomain_ready"`
  // because stage flips before all timestamps are stamped — the timeline
  // would briefly show "ready" with grey rows. All-timestamps-set is the
  // honest signal, identical to what the timeline component checks.
  const allReady =
    !!status &&
    !!status.cname_created_at &&
    !!status.domain_alias_claimed_at &&
    !!status.cname_resolved_at &&
    !!status.https_reachable_at &&
    !!status.subdomain_ready_at;

  const poll = useCallback(async () => {
    if (!onboardingId || !idToken) return;
    if (inFlightRef.current) return; // skip overlapping requests
    inFlightRef.current = true;
    setRefreshing(true);
    try {
      const res = await onboardingApi.getDnsStatus(onboardingId, idToken);
      setStatus(res);
      setError(null);
      setLastRefreshedAt(Date.now());
    } catch (err) {
      const message = err instanceof Error ? err.message : "Status check failed";
      setError(message);
    } finally {
      inFlightRef.current = false;
      setRefreshing(false);
    }
  }, [onboardingId, idToken]);

  useEffect(() => {
    if (!onboardingId || !idToken) return;
    poll(); // immediate first refresh on mount
    const id = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [onboardingId, idToken, poll]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div
            className={
              "rounded-md p-2 " +
              (allReady
                ? "bg-signal-ok/15 text-signal-ok"
                : "bg-brand-violet/15 text-brand-violet")
            }
          >
            {allReady ? (
              <Mail className="h-5 w-5" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin" />
            )}
          </div>
          <div>
            <CardTitle>
              {allReady ? "Check your email" : "Setting up your workspace"}
            </CardTitle>
            <CardDescription>
              {allReady ? (
                <>
                  We've sent a workspace-ready email to{" "}
                  <span className="font-mono text-foreground">{email ?? "your inbox"}</span>.
                  Click the link in that email to open your custom workspace at{" "}
                  <span className="font-mono text-foreground">
                    {subdomain ?? "your-workspace"}.virtualdesk.ai
                  </span>
                  . Closing this tab is fine.
                </>
              ) : (
                "We're provisioning your subdomain. This takes a minute. You can leave this tab — we'll email you the moment it's reachable."
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <OnboardingTimeline items={itemsFromDnsStatus(status, subdomain)} />

        {status?.dns_lookup ? <DnsLookupPanel lookup={status.dns_lookup} /> : null}

        {error && (
          <div className="flex items-start gap-2 rounded-md bg-signal-failed/10 px-3 py-2 text-xs text-signal-failed">
            <AlertTriangle className="h-4 w-4 mt-[2px]" />
            <span>Status check hiccupped — we'll retry shortly. ({error})</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-border/40 pt-4 text-xs text-muted-foreground">
          <span aria-live="polite">
            {refreshing
              ? "Refreshing…"
              : lastRefreshedAt
              ? `Auto-refreshing every 30 seconds · last checked ${formatRelative(lastRefreshedAt)}`
              : "Auto-refreshing every 30 seconds"}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={poll}
            disabled={refreshing}
            className="border-white/15 text-foreground hover:bg-white/5"
          >
            <RefreshCw className={refreshing ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
            Refresh now
          </Button>
        </div>

        {allReady && (
          <div className="flex items-start gap-2 rounded-md bg-signal-ok/10 px-3 py-2.5 text-xs text-signal-ok">
            <CheckCircle2 className="h-4 w-4 mt-[2px]" />
            <span>
              Your workspace is live. Use the email link to sign in — every
              click is tracked so we know your invitation reached you.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// DnsLookupPanel — dig-style live snapshot of the subdomain. Updates on every
// /dns-status poll. Shows the resolved CNAME target and IP addresses, or the
// raw lookup error if propagation hasn't reached our resolver yet. The user
// can compare this to what their own browser sees.
function DnsLookupPanel({ lookup }: { lookup: DnsLookupResult }) {
  const lookupErr = lookup.error || lookup.cname_error || lookup.addresses_error;
  const queriedAt = (() => {
    try {
      return new Date(lookup.queried_at).toLocaleTimeString();
    } catch {
      return lookup.queried_at;
    }
  })();
  const cnameLine = lookup.cname
    ? `${lookup.hostname}.\t300\tIN\tCNAME\t${lookup.cname}.`
    : null;
  const addrLines = (lookup.addresses ?? []).map((addr) => {
    const isV6 = addr.includes(":");
    return `${lookup.hostname}.\t300\tIN\t${isV6 ? "AAAA" : "A"}\t${addr}`;
  });
  const hasResults = !!cnameLine || addrLines.length > 0;
  return (
    <div className="rounded-md border border-border/40 bg-muted/20 p-3 text-xs">
      <div className="mb-2 flex items-center justify-between text-muted-foreground">
        <span className="font-mono">$ dig +short {lookup.hostname}</span>
        <span>queried {queriedAt}</span>
      </div>
      {hasResults ? (
        <pre className="overflow-x-auto whitespace-pre font-mono text-[11px] text-foreground">
{[cnameLine, ...addrLines].filter(Boolean).join("\n")}
        </pre>
      ) : (
        <p className="font-mono text-[11px] text-muted-foreground">
          ;; no records yet — waiting for DNS to propagate
        </p>
      )}
      {lookupErr ? (
        <p className="mt-2 font-mono text-[11px] text-signal-failed">
          ;; lookup error: {lookupErr}
        </p>
      ) : null}
    </div>
  );
}

function formatRelative(ts: number): string {
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  return `${mins}m ago`;
}
